/**
 * Classement FairView d'une campagne : score global pondéré par candidat,
 * contre le barème de son template.
 *
 * Partagé par l'onglet Matching de la campagne et par la fiche candidat, pour
 * que le score et le rang d'un candidat soient les mêmes sur les deux écrans.
 *
 * Le référentiel est ici le BARÈME DU TEMPLATE — les poids par compétence saisis
 * à la conception de l'entretien — et non un référentiel de référents comme en
 * PAMI ou fit-culturel : l'entretien structuré porte déjà sa cible.
 */
import { useMemo } from "react";
import type {
  EvaluationData,
  LigneMatchingEntStruct,
} from "@repo/results";
import { calculerScores, lignesMatchingEntStruct } from "@repo/utils";

/**
 * Signature du barème d'un candidat : compétences ET poids, triés. Deux
 * candidats ne sont comparables que si elle est identique — un même score global
 * calculé sur deux barèmes différents ne classe rien.
 */
function signatureBareme(lignes: LigneMatchingEntStruct[]): string {
  return lignes
    .map((l) => `${l.competence}:${l.poids}`)
    .sort()
    .join("|");
}

export type EntreeClassementEntStruct = {
  evaluation: EvaluationData;
  /** Moyenne pondérée des taux de couverture, sur 100. `null` si non calculable. */
  score: number | null;
  lignes: LigneMatchingEntStruct[];
  signature: string;
};

export type ClassementEntStruct = {
  /** Trié par score décroissant, les candidats sans score en fin de liste. */
  classement: EntreeClassementEntStruct[];
  /**
   * Barèmes hétérogènes : deux candidats passés sur des templates différents ne
   * sont pas comparables entre eux.
   */
  baremesHeterogenes: boolean;
};

export function useClassementEntStruct(
  evaluations: EvaluationData[] | undefined,
): ClassementEntStruct {
  /**
   * Classement trié sur le score global PONDÉRÉ, décroissant. Les critères non
   * posés restent dans le maximum : `calculerScores` ne compte que les critères
   * `covered`, donc un entretien écourté pèse sur le score — ce qui est
   * l'intention en classement, où l'on compare ce qui a été prouvé.
   *
   * Les candidats sans score exploitable finissent la liste (`-1` est hors de
   * l'intervalle 0-100 atteignable par un score réel).
   */
  const classement = useMemo<EntreeClassementEntStruct[]>(() => {
    if (!evaluations) return [];

    const entrees = evaluations.map((evaluation) => {
      const scores = evaluation.questionsEtat
        ? calculerScores(evaluation.questionsEtat)
        : null;
      if (!scores || scores.scoreGlobalMax <= 0) {
        return { evaluation, score: null, lignes: [], signature: "" };
      }
      // La compétence neutre, hors barème, est écartée : la signature du
      // template ne dépend pas de sa présence.
      const lignes = lignesMatchingEntStruct(scores);
      return {
        evaluation,
        // `scoreGlobalObtenu / scoreGlobalMax` = Σ(taux × poids) / Σ poids : la
        // pondération est déjà dedans, inutile de la réappliquer.
        score: (scores.scoreGlobalObtenu / scores.scoreGlobalMax) * 100,
        lignes,
        signature: signatureBareme(lignes),
      };
    });

    return entrees.sort((a, b) => (b.score ?? -1) - (a.score ?? -1));
  }, [evaluations]);

  /**
   * Barèmes hétérogènes : deux candidats passés sur des templates différents ne
   * sont pas comparables. On le DIT plutôt que d'afficher un classement qui
   * mélangerait des échelles — un score de 70 % sur cinq compétences et un 70 %
   * sur douze ne disent pas la même chose.
   */
  const baremesHeterogenes = useMemo(() => {
    const signatures = new Set(
      classement.filter((e) => e.score !== null).map((e) => e.signature),
    );
    return signatures.size > 1;
  }, [classement]);

  return { classement, baremesHeterogenes };
}
